import React, { useState, useEffect } from 'react';
import {
  Typography,
  Paper,
  Box,
  Button,
  TextField,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Snackbar,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import api from '../../utils/api';
import logger from '../../utils/logger';

const MechanicsSettings = () => {
  const [mechanics, setMechanics] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedMechanic, setSelectedMechanic] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    userId: '',
    hourlyRate: '',
    phoneNumber: '',
    specialization: '',
    notes: '',
    isActive: true
  });
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    fetchMechanics();
    fetchUsers();
  }, []);

  const fetchMechanics = async () => {
    setLoading(true);
    try {
      const res = await api.get('/mechanics');
      logger.debug('Meccanici ricevuti:', res.data.length);
      setMechanics(res.data);
    } catch (err) {
      logger.error('Error fetching mechanics:', err);
      showSnackbar('Errore nel caricamento dei meccanici', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchUsers = async () => {
    try {
      const res = await api.get('/users/mechanics');
      setUsers(res.data);
    } catch (err) {
      logger.error('Error fetching users:', err);
      showSnackbar('Errore nel caricamento degli utenti', 'error');
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.userId) {
      errors.userId = 'Seleziona un utente';
    }
    if (!formData.hourlyRate || isNaN(formData.hourlyRate) || parseFloat(formData.hourlyRate) <= 0) {
      errors.hourlyRate = 'Inserisci una tariffa oraria valida';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleOpenDialog = (mechanic = null) => {
    if (mechanic) {
      setSelectedMechanic(mechanic);
      setFormData({
        userId: mechanic.userId,
        hourlyRate: mechanic.hourlyRate?.toString() || '',
        phoneNumber: mechanic.phoneNumber || '',
        specialization: mechanic.specialization || '',
        notes: mechanic.notes || '',
        isActive: mechanic.isActive
      });
    } else {
      setSelectedMechanic(null);
      setFormData({
        userId: '',
        hourlyRate: '',
        phoneNumber: '',
        specialization: '',
        notes: '',
        isActive: true
      });
    }
    setFormErrors({});
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedMechanic(null);
    setFormData({
      userId: '',
      hourlyRate: '',
      phoneNumber: '',
      specialization: '',
      notes: '',
      isActive: true
    });
    setFormErrors({});
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      const dataToSend = {
        ...formData,
        hourlyRate: parseFloat(formData.hourlyRate)
      };
      logger.debug('Salvataggio meccanico');
      if (selectedMechanic) {
        await api.put(`/mechanics/${selectedMechanic.id}`, dataToSend);
        showSnackbar('Meccanico aggiornato con successo', 'success');
      } else {
        await api.post('/mechanics', dataToSend);
        showSnackbar('Meccanico creato con successo', 'success');
      }
      handleCloseDialog();
      fetchMechanics();
    } catch (err) {
      logger.error('Error saving mechanic:', err);
      const errorMessage = err.response?.data?.msg || 'Errore nel salvataggio del meccanico';
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Sei sicuro di voler disattivare questo meccanico?')) {
      setLoading(true);
      try {
        await api.delete(`/mechanics/${id}`);
        showSnackbar('Meccanico disattivato con successo', 'success');
        fetchMechanics();
      } catch (err) {
        logger.error('Error deleting mechanic:', err);
        showSnackbar('Errore nella disattivazione del meccanico', 'error');
      } finally {
        setLoading(false);
      }
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">Gestione Meccanici</Typography>
        <Button 
          variant="contained" 
          color="primary"
          startIcon={<AddIcon />} 
          onClick={() => handleOpenDialog()}
        >
          Nuovo Meccanico
        </Button>
      </Box>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Email</TableCell>
                <TableCell>Telefono</TableCell>
                <TableCell>Tariffa Oraria</TableCell>
                <TableCell>Specializzazione</TableCell>
                <TableCell>Stato</TableCell>
                <TableCell>Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : mechanics.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Nessun meccanico trovato
                  </TableCell>
                </TableRow>
              ) : (
                mechanics.map((mechanic) => (
                  <TableRow key={mechanic.id}>
                    <TableCell>{mechanic.User?.name}</TableCell>
                    <TableCell>{mechanic.User?.email}</TableCell>
                    <TableCell>{mechanic.phoneNumber}</TableCell>
                    <TableCell>€{mechanic.hourlyRate}/h</TableCell>
                    <TableCell>{mechanic.specialization}</TableCell>
                    <TableCell>{mechanic.isActive ? 'Attivo' : 'Disattivato'}</TableCell>
                    <TableCell>
                      <IconButton
                        color="primary"
                        onClick={() => handleOpenDialog(mechanic)}
                        disabled={!mechanic.isActive}
                      >
                        <EditIcon />
                      </IconButton>
                      <IconButton
                        color="error"
                        onClick={() => handleDelete(mechanic.id)}
                        disabled={!mechanic.isActive}
                      >
                          <DeleteIcon />
                        </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {selectedMechanic ? 'Modifica Meccanico' : 'Nuovo Meccanico'}
        </DialogTitle>
        <form onSubmit={handleSubmit}>
        <DialogContent>
            <Grid container spacing={2}>
            <Grid item xs={12}>
                <FormControl fullWidth required error={!!formErrors.userId}>
                  <InputLabel>Utente</InputLabel>
                <Select
                  name="userId"
                  value={formData.userId}
                  onChange={handleInputChange}
                  disabled={!!selectedMechanic}
                >
                  {users.map(user => (
                    <MenuItem key={user.id} value={user.id}>
                      {user.name} ({user.email})
                    </MenuItem>
                  ))}
                </Select>
                  {formErrors.userId && (
                    <Typography color="error" variant="caption">
                      {formErrors.userId}
                    </Typography>
                  )}
              </FormControl>
            </Grid>
              <Grid item xs={12}>
              <TextField
                name="hourlyRate"
                  label="Tariffa Oraria (€/h)"
                type="number"
                value={formData.hourlyRate}
                onChange={handleInputChange}
                  fullWidth
                required
                  error={!!formErrors.hourlyRate}
                  helperText={formErrors.hourlyRate}
                  inputProps={{ step: "0.01", min: "0" }}
              />
            </Grid>
              <Grid item xs={12}>
              <TextField
                name="phoneNumber"
                  label="Telefono"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="specialization"
                label="Specializzazione"
                value={formData.specialization}
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                name="notes"
                label="Note"
                value={formData.notes}
                onChange={handleInputChange}
                  fullWidth
                multiline
                  rows={4}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Annulla</Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={loading}
            >
              {loading ? <CircularProgress size={24} /> : 'Salva'}
          </Button>
        </DialogActions>
        </form>
      </Dialog>

      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default MechanicsSettings;